import { useCallback, useRef, useState } from "react";
import { Upload, X, FileText, FileSpreadsheet, FileImage, File as FileIcon, AlertCircle } from "lucide-react";

const ALLOWED_MIME = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/csv",
  "image/png",
  "image/jpeg",
]);
const MAX_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_FILES = 5;

const humanSize = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

const iconFor = (mime) => {
  if (mime === "application/pdf") return FileText;
  if (mime.includes("sheet") || mime === "text/csv" || mime.includes("excel")) return FileSpreadsheet;
  if (mime.startsWith("image/")) return FileImage;
  return FileIcon;
};

/**
 * Drag-and-drop file picker for RFP attachments.
 * Props:
 *  - value: File[]
 *  - onChange: (File[]) => void
 */
export default function FileDropzone({ value = [], onChange }) {
  const [drag, setDrag] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const addFiles = useCallback((incoming) => {
    setError("");
    const next = [...value];
    for (const f of incoming) {
      if (next.length >= MAX_FILES) {
        setError(`Maximum ${MAX_FILES} files.`);
        break;
      }
      if (!ALLOWED_MIME.has(f.type)) {
        setError(`${f.name}: unsupported type. Allowed: PDF, DOC(X), XLS(X), TXT, CSV, PNG, JPG.`);
        continue;
      }
      if (f.size > MAX_SIZE) {
        setError(`${f.name}: exceeds 10 MB.`);
        continue;
      }
      if (next.some((x) => x.name === f.name && x.size === f.size)) continue; // dedupe
      next.push(f);
    }
    onChange(next);
  }, [value, onChange]);

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    addFiles(Array.from(e.dataTransfer.files || []));
  };

  const remove = (idx) => {
    const next = value.slice();
    next.splice(idx, 1);
    onChange(next);
    setError("");
  };

  return (
    <div className="w-full">
      <label className="block text-xs font-mono uppercase tracking-[0.24em] text-charcoal-soft mb-2">
        Attachments <span className="text-charcoal-soft/60 normal-case tracking-normal">(optional — PDF, DOC, XLS, TXT, CSV, PNG, JPG · max {MAX_FILES} files · 10 MB each)</span>
      </label>

      <div
        data-testid="rfp-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
        className={`relative border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
          drag ? "border-gold bg-gold/5" : "border-gray-300 hover:border-gold hover:bg-offwhite"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.png,.jpg,.jpeg"
          data-testid="rfp-file-input"
          onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ""; }}
          className="hidden"
        />
        <div className="w-11 h-11 mx-auto bg-charcoal grid place-items-center">
          <Upload className="w-5 h-5 text-gold" />
        </div>
        <div className="mt-4 font-heading text-charcoal">
          <span className="text-gold underline underline-offset-4">Click to browse</span> or drag files here
        </div>
        <div className="mt-1 text-xs text-charcoal-soft font-mono tracking-wide">
          Attach the solicitation, SOW, or any supporting docs
        </div>
      </div>

      {error && (
        <div data-testid="rfp-file-error" className="mt-3 flex items-start gap-2 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {value.length > 0 && (
        <ul data-testid="rfp-file-list" className="mt-4 divide-y divide-gray-200 border border-gray-200">
          {value.map((f, i) => {
            const Icon = iconFor(f.type);
            return (
              <li key={`${f.name}-${i}`} data-testid={`rfp-file-item-${i}`} className="flex items-center gap-4 p-3">
                <div className="w-9 h-9 border border-gray-200 grid place-items-center shrink-0">
                  <Icon className="w-4 h-4 text-gold" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-charcoal truncate">{f.name}</div>
                  <div className="text-xs text-charcoal-soft font-mono">{humanSize(f.size)}</div>
                </div>
                <button
                  type="button"
                  data-testid={`rfp-file-remove-${i}`}
                  onClick={(e) => { e.stopPropagation(); remove(i); }}
                  aria-label={`Remove ${f.name}`}
                  className="w-8 h-8 grid place-items-center border border-gray-200 hover:border-charcoal hover:bg-charcoal hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
